import { GetStaticProps } from 'next';
import Link from 'next/link';

import BlogArticle from '../../../components/blog/BlogArticle';
import { getBlogPost } from '../../../content/blog/posts';
import { DefaultLocale } from '../../../providers/tolgee';

const SharingTheSparkAGuideToPresentingCuripodWithConfidence = () => {
  const post = getBlogPost(
    'sharing-the-spark-a-guide-to-presenting-curipod-with-confidence',
  );
  return (
    <BlogArticle post={post}>
      <p>
        {
          'Have you seen how Curipod changes the energy in your classroom—how it helps students engage, think more deeply, and find their voice?'
        }
      </p>
      <p>
        {
          "If you're ready to share that transformation with others—at a staff meeting, district PD, or education conference—this guide will help you plan a session that resonates."
        }
      </p>
      <p>
        {
          'At Curipod, we believe in classrooms full of curiosity, discussion, and connection. That’s the experience we hope every educator walks away ready to create.'
        }
      </p>
      <h2>{'How to Present the Curipod Way'}</h2>
      <h3>{'1. Lead with Impact: Tell the story of your classroom.'}</h3>
      <p>
        {
          'Curipod’s mission is to build classrooms students remember—full of conversation, curiosity, and connection.'
        }
      </p>
      <p>
        {
          'The most powerful Curipod presentations begin with a story. A moment. A shift. Share a moment from your classroom. Maybe it was a quiet student who typed something powerful. A class that couldn’t stop talking. A teacher who felt excited to teach again. These are the moments that stick with us—and they’re what other educators want to hear.'
        }
      </p>
      <p>
        <Link href="/c/blog-posts/why-were-betting-on-learning-together" passHref>
          <a>{'We take a strong stance on learning.'}</a>
        </Link>
        {
          ' Curipod isn’t about passive AI use or about individualized personalized click-through learning journeys—It’s about sparking discussion, building community, and creating classrooms students remember.'
        }
      </p>
      <h3>{'2. Curipod stands on its own. Don’t bundle it with other AI tools.'}</h3>
      <p>
        <Link
          href="/c/blog-posts/the-problem-with-ai-lesson-plans-and-how-curipod-is-different"
          passHref
        >
          <a>{'Curipod is different by design.'}</a>
        </Link>
        {
          'It’s not a generic AI tool or a time-saver—it’s a teacher-paced, discussion-driven platform designed to engage whole classrooms in meaningful learning.'
        }
      </p>
      <p>
        {
          'In your session, let Curipod take center stage. Focus on how it changes the classroom experience—for you and for your students.'
        }
      </p>
      <p>
        {
          'Avoid bundling it into long lists of AI tools. Curipod’s value is in the real-time feedback, whole-class engagement, and student voice it brings to every lesson. No mashups. No long lists of AI tools. Let the student experience speak for itself.'
        }
      </p>
      <h3>{'3. Highlight the features that matter: Full Lesson and Curify My Slides'}</h3>
      <p>
        {
          'Over the past few years, we’ve learned what teachers love—and what makes the biggest difference in the classroom.'
        }
      </p>
      <p>{'That’s why we’re focused on three key features:'}</p>
      <ul>
        <li>{'Full Lessons'}</li>
        <li>{'Curify My Slides'}</li>
        <li>{'Test Prep'}</li>
      </ul>
      <p>
        {
          'They’re designed to give teachers a strong, structured starting point grounded in research and best practices.'
        }
      </p>
      <p>{'Why the shift?'}</p>
      <p>
        {
          'We want every teacher to get the strongest possible lesson built on best practices in education. By focusing on these key features, we ensure every experience with Curipod is research-based and classroom-ready. We’ve built in creativity, customization, and real-time feedback—without adding hours of planning.'
        }
      </p>
      <p>
        {
          'Curipod makes the best thing to do the easiest thing to do - which is what we want your colleagues to walk away knowing!'
        }
      </p>
      <h3>{'4. Let teachers experience Curipod as a student'}</h3>
      <p>
        {
          'Curipod is something you have to feel to understand. Start your session with a short Curipod activity. Let teachers respond, vote, draw, and reflect—just like their students would.'
        }
      </p>
      <p>
        {
          'When they participate as learners, they’ll experience firsthand how Curipod builds energy and engagement.'
        }
      </p>
      <p>
        {'Check out our '}
        <a
          href={
            'https://drive.google.com/file/d/1egiMXa2F_lwEBjgfjyaPMMwVUaN15dMx/view?usp=sharing'
          }
          target="_blank"
          rel="noopener noreferrer"
        >
          {'PD Kit'}
        </a>
        {
          ' that includes speaker notes and a Curipod lesson you can use to run your training!'
        }
      </p>
      <h3>{'5. Give them time to build a lesson and reflect'}</h3>
      <p>
        {
          'End your session by giving participants time to explore Curipod’s tools and build a lesson of their own.'
        }
      </p>
      <p>
        <strong>{'Use prompts like:'}</strong>
      </p>
      <ul>
        <li>{'What will this lead to in your classroom?'}</li>
        <li>
          {'How will it help students talk more, write more, or show what they know?'}
        </li>
        <li>{"How is this different from the tools you're using now?"}</li>
      </ul>
      <p>
        {
          'Even just Curifying a few slides can be enough to get started. The goal is to help them walk away with something they feel confident using tomorrow.'
        }
      </p>
      <h3>
        <strong>{'Final Note:'}</strong>
        {'Leave them ready to spread the word.'}
      </h3>
      <p>
        {'Your session isn’t just about training—it’s about starting a ripple effect.'}
      </p>
      <p>
        {
          'When teachers leave feeling energized, they’ll tell a colleague. Share a lesson. Recommend it in a team meeting. That’s how Curipod grows: teacher to teacher, school to school.'
        }
      </p>
      <p>{'End every session with a clear next step:'}</p>
      <ul>
        <li>
          <Link href="/c/training-and-professional-development" passHref>
            <a>{'Book a time with the Curipod team for help.'}</a>
          </Link>
        </li>
        <li>{'Host your own PD.'}</li>
        <li>{'Share a favorite lesson with a teacher friend.'}</li>
      </ul>
      <p>
        {
          'We want every person who joins your session to leave ready to tell someone else, “You have to try this!”'
        }
      </p>
      <p>{'Let’s grow curiosity-driven classrooms—together.'}</p>
    </BlogArticle>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default SharingTheSparkAGuideToPresentingCuripodWithConfidence;
